/**
 * 日本語の「近くで探す（near me）」コピー — 主要9クラスター。
 *
 * 手書き（生成時・実行時ともAIクレジット消費ゼロ）。事実関係は
 * ./registry.ts / ./en-copy.ts / ./zh-copy.ts と整合させています。
 *  - タイはハーグ・アポスティーユ条約の締約国ではないため、国外で使用する
 *    タイの公文書は外務省領事局の認証（Legalization）を受け、必要に応じて
 *    在タイ当該国大使館の認証を追加します。
 *  - 料金は掲載せず案件ごとの見積り。処理期間・受理の保証はしません。
 *    受理するか否かは提出先機関が判断します。
 */

import type { NearServiceKey } from "./registry";

export type NearJaCopy = {
  /** H1・タイトルで使う短いサービス名。 */
  label: string;
  /** アンサーファースト用の事実ベースの定義（1〜2文）。 */
  definition: string;
  /** 手続きの流れ（順序どおり）。 */
  chain: string[];
  /** 準備する書類。 */
  checklist: string[];
  /** よくある差し戻し理由・注意点。 */
  pitfalls: string[];
  /** 遠隔対応の範囲。 */
  remote: string;
};

export const NEAR_JA_KEYS = [
  "notary",
  "naati",
  "sworn",
  "pcc",
  "translation",
  "moj",
  "visa",
  "lgz",
  "apostille",
] as const satisfies readonly NearServiceKey[];

export type NearJaKey = (typeof NEAR_JA_KEYS)[number];

const LEGALIZE_CHAIN =
  "提出先が求める場合は、外務省領事局（Legalization Division）の認証を受け、その後に在タイ当該国大使館の認証を追加します。";

export const NEAR_JA_COPY: Record<NearJaKey, NearJaCopy> = {
  notary: {
    label: "タイの公証業務弁護士（Notarial Services Attorney）",
    definition:
      "タイには独立した公証人制度がありません。署名認証・写し証明・宣誓供述書・会社の授権書類の認証は、タイ弁護士会（Lawyers Council of Thailand）の認可を受けた公証業務弁護士（Notarial Services Attorney）が行い、国外での使用に用いられます。",
    chain: [
      "書類一式のカラースキャンを送り、提出先機関または使用する国を伝えます。",
      "どのページに実際に認証が必要か、正しい順序はどれかを当方で確認します。",
      "署名者が公証業務弁護士の面前で署名します（本人出席、または提出先が認める方法）。",
      "認証文言の記載・押印・登録簿への記録を行います。",
      LEGALIZE_CHAIN,
      "認証済み原本を宅配便で返送、または事務所で受け取ります。",
    ],
    checklist: [
      "署名者全員のパスポートまたはタイIDカード（有効期限内・書類と綴りが一致）。",
      "認証対象書類の最終版（未署名の状態）。",
      "会社名義の場合は会社証明書／DBD登記抄本と授権取締役の一覧。",
      "他人に代わって署名する場合は委任状（Power of Attorney）。",
      "提出先から書面で要件を受け取っている場合はその写し。",
    ],
    pitfalls: [
      "認証の予約前に署名してしまい、原本を作り直すことになる。",
      "氏名の綴りがパスポートと一致しない — 国外での差し戻し理由として最も多い。",
      "タイの書類にアポスティーユが取れると誤解する。通常は外務省認証＋大使館認証の経路です。",
      "提出先が認証済み原本を求めているのに、認証済みの写しだけを提出する。",
      "会社登記抄本が古い。多くの機関は直近発行のものを要求します。",
    ],
    remote:
      "書類確認・文言のチェック・その後の認証工程は遠隔で対応できます。面前署名の工程のみ立会いが必要です。",
  },
  naati: {
    label: "NAATI認定翻訳",
    definition:
      "NAATI認定翻訳は、オーストラリアのNAATI資格を持つ翻訳者が作成し、翻訳者の認証文と印を付した翻訳です。オーストラリアの政府機関・大学・ビザ審査センターで一般に要求されます。",
    chain: [
      "カラースキャンを送付。鮮明さ・印影・ページ数を当方で点検します。",
      "提出先（内務省、大学、資格認定機関など）とパスポート上の氏名表記を確認します。",
      "NAATI認定翻訳者が翻訳し、認証文と印を付します。",
      "原本と1項目ずつ照合します（氏名・日付・数字・印影の文字）。",
      "電子認証版を納品し、必要に応じて紙の原本を発送します。",
    ],
    checklist: [
      "パスポートの身分事項ページ（ローマ字表記の確認用）。",
      "各書類の全ページカラースキャン（裏面・印影を含む）。",
      "過去に提出した旧訳（あれば）— 用語を統一するため。",
      "提出先から示された翻訳に関する具体的要件（受領済みの場合）。",
    ],
    pitfalls: [
      "スキャンの見切れで公印が写っておらず、翻訳一式が無効になる。",
      "同じ氏名が書類ごとに違う綴りになっている。",
      "先に認証済み原本の取得が必要なのに、翻訳を先に行ってしまう。",
      "グレースケールのコピーで、印影の色を確認できない。",
    ],
    remote: "全工程オンラインで完了でき、来所は不要です。",
  },
  sworn: {
    label: "宣誓翻訳者／裁判所登録翻訳者による翻訳",
    definition:
      "国によっては宣誓翻訳者または公的名簿に登録された翻訳者の訳文のみを受理します。タイでは該当機関または裁判所の名簿に登載された翻訳者が作成し、認証ページに翻訳者の身分と原本情報を記載します。",
    chain: [
      "提出先の国がどの名簿（裁判所・大使館・省の名簿）を認めるかを確認します。",
      "スキャンの鮮明さと欠落ページの有無を点検します。",
      "登録翻訳者が翻訳し、認証ページを作成します。",
      LEGALIZE_CHAIN,
      "押印済みの紙の書類を返送します。",
    ],
    checklist: [
      "氏名の転写に用いるパスポートまたはIDカード。",
      "添付書類と全ての印影を含む完全なスキャン。",
      "提出先機関名と必要な言語の組み合わせ。",
      "提出先が定める期限（ある場合）。",
    ],
    pitfalls: [
      "名簿の種類を誤ると、訳文が正しくても差し戻されます。",
      "同じ一件の中で人名・地名の訳が統一されていない。",
      "添付ページが欠けており、提出先が書類不備と判断する。",
      "先に製本・契印してしまい、その後の認証工程ができなくなる。",
    ],
    remote: "翻訳と認証の工程は遠隔で進められます。紙の原本は宅配便で納品します。",
  },
  pcc: {
    label: "タイの無犯罪証明書（Police Clearance Certificate）",
    definition:
      "タイの無犯罪証明書は王立タイ警察の犯罪記録部門が発行します。ビザ・移民・就労・資格申請に用いられ、通常は認証翻訳とその後の認証工程が併せて必要になります。",
    chain: [
      "提出先が求める証明書の種類と使用目的の記載を確認します。",
      "申請書類を準備し、規定どおりに指紋カードを採取します。",
      "犯罪記録部門へ申請します。",
      "受領後、認証翻訳者が翻訳します。",
      LEGALIZE_CHAIN,
    ],
    checklist: [
      "パスポート（タイの出入国スタンプを含む）またはタイIDカード。",
      "タイ国内の住所証明、または住所に関する証明書。",
      "指紋カード（機関の規定に沿って採取したもの）。",
      "使用目的を示す申請書または依頼状（ビザ・移民・就労など）。",
      "代理申請の場合は委任状。",
    ],
    pitfalls: [
      "パスポートと旧書類で氏名の綴りが異なり、記録照合ができない。",
      "指紋の品質が基準を満たさず、採り直しになる。",
      "使用目的の記載が提出先の要件と合わず、再申請になる。",
      "提出先が定める有効期間（多くは3〜6か月）を過ぎている。",
    ],
    remote:
      "申請準備・翻訳・その後の認証は遠隔で支援できます。指紋採取は本人の出席が必要です。",
  },
  translation: {
    label: "認証翻訳（タイ語・英語ほか多言語）",
    definition:
      "認証翻訳には、訳文が原本と一致することを示す翻訳者または翻訳会社の宣言文が付きます。タイの官公署・大使館・海外機関が書類を受理するための基本工程です。",
    chain: [
      "カラースキャンを送り、提出先機関と使用する国を伝えます。",
      "氏名・地名・固有名詞の表記を統一する方針を確認します。",
      "翻訳を作成し、認証文と印を付します。",
      "原本と照合して氏名・日付・番号・印影の文字を検証します。",
      LEGALIZE_CHAIN,
    ],
    checklist: [
      "氏名の綴りを確認するためのパスポートまたはIDカード。",
      "書類の完全なカラースキャン（裏面・添付・印影を含む）。",
      "提出先機関名と必要言語。",
      "旧訳または用語集（あれば）。",
    ],
    pitfalls: [
      "一部のページだけ翻訳したが、提出先は全ページを要求していた。",
      "住所や学位名などの固有名詞の訳が前後で不一致。",
      "有効期限切れの原本（住民登録抄本・会社登記抄本など）を翻訳してしまう。",
      "スキャンが不鮮明で印影の文字が判読できない。",
    ],
    remote: "全工程オンラインで対応。紙の認証版は必要に応じて発送します。",
  },
  moj: {
    label: "司法省・官公署関連の書類手続き",
    definition:
      "タイ司法省および関連機関が扱う書類（記録照会、証明書、機関発行の公文書）は、機関が定める様式・授権・提出経路に従って手続きし、その後に翻訳と認証が必要になることが多いです。",
    chain: [
      "必要な書類をどの機関・部署が発行するのかを特定します。",
      "申請書・身分証明書・委任状を準備します。",
      "所定の経路で提出し、進行状況を追跡します。",
      "受領後、認証翻訳者が翻訳します。",
      LEGALIZE_CHAIN,
    ],
    checklist: [
      "申請人のパスポートまたはタイIDカード。",
      "代理申請に必要な委任状と代理人の身分証明書。",
      "申請理由に関係する既存の書類または事件番号。",
      "提出先からの書面要件（ある場合）。",
    ],
    pitfalls: [
      "誤った機関・部署へ提出し、一巡分の時間を失う。",
      "委任状の文言が今回の申請事項を含んでいない。",
      "発行後に翻訳・認証を進めず、有効期間が迫ってしまう。",
      "すべての公文書が即日交付されると考えてしまう（多くは即日ではありません）。",
    ],
    remote:
      "書類準備・翻訳・認証は遠隔で対応できます。一部の機関は本人または受任者の出席を求めます。",
  },
  visa: {
    label: "ビザ書類の準備とコンサルティング",
    definition:
      "ビザ書類の準備とは、お手元の証拠書類を渡航先が定める形に整えることです。認証翻訳、資金・関係性の証明、必要な場合の公証と認証が含まれます。審査の権限は常に大使館または移民当局にあります。",
    chain: [
      "ビザの種類と、その大使館が現在公表しているリストを確認します。",
      "既存の書類を点検し、不足と再取得が必要なものを洗い出します。",
      "認証翻訳を作成し、必要に応じて公証を行います。",
      LEGALIZE_CHAIN,
      "審査担当者が読みやすい順序でファイルにまとめます。",
    ],
    checklist: [
      "パスポート（過去のビザ・出入国スタンプを含む）。",
      "身分関係書類（出生・婚姻・住民登録など、該当するもの）。",
      "資金の証明（申請区分に応じた書式）。",
      "関係性の証明（家族・配偶者ビザの場合）。",
      "大使館が公表している最新のチェックリスト。",
    ],
    pitfalls: [
      "古いチェックリストを使い、書式が変更されていたことに気づかない。",
      "受理期間を過ぎた古い証明書を提出する。",
      "翻訳の氏名表記がパスポートと一致しない。",
      "ビザの承認を保証する業者を信じてしまう。承認は当局のみが決定します。",
    ],
    remote:
      "書類の点検・翻訳・並べ方の設計は全て遠隔で対応できます。面接や生体情報の採取は本人が出席します。",
  },
  lgz: {
    label: "外務省領事局による認証（Legalization）",
    definition:
      "国外で使用するタイの公文書は、外務省領事局（Legalization Division）で署名・印影の真正性の認証を受けます。タイはアポスティーユ条約の締約国ではないため、多くの場合はさらに在タイ当該国大使館の認証が必要です。",
    chain: [
      "書類の種類ごとに、認証可能な形式（原本・認証済み写し・翻訳）を確認します。",
      "必要に応じて発行機関から最新の原本を取得します。",
      "認証翻訳を作成します（多くの場合はタイ語⇄英語）。",
      "外務省領事局へ提出し、認証を受けます。",
      "提出先が求める場合は、在タイ当該国大使館の認証を追加します。",
    ],
    checklist: [
      "認証対象の原本または発行機関の認証済み写し。",
      "申請人または受任者のパスポート・IDカード。",
      "代理提出の場合は委任状。",
      "認証翻訳（対象書類に必要な場合）。",
      "使用する国と提出先機関の名称。",
    ],
    pitfalls: [
      "アポスティーユを取得しようとする — タイでは発行されません。",
      "認証対象として認められない形式の写しを提出する。",
      "翻訳の書式が要件に合わず、認証段階で差し戻される。",
      "大使館認証が別工程であることを見落とし、日程が足りなくなる。",
    ],
    remote:
      "書類準備・翻訳・提出代行は遠隔で対応できます。委任状の署名にはご本人の関与が必要です。",
  },
  apostille: {
    label: "アポスティーユ／認証（タイの書類の場合）",
    definition:
      "タイはハーグ・アポスティーユ条約の締約国ではないため、タイの公文書にアポスティーユは付与されません。同等の効果を得る経路は、外務省領事局の認証と、必要に応じた在タイ当該国大使館の認証です。海外発行の書類は、その発行国でアポスティーユを取得できます。",
    chain: [
      "書類がタイ発行か海外発行かを切り分けます（経路が全く異なります）。",
      "タイ発行の場合：最新の原本を取得し、認証翻訳を作成します。",
      "外務省領事局の認証を受けます。",
      "提出先が求める場合は在タイ当該国大使館の認証を追加します。",
      "海外発行の場合：発行国でアポスティーユを取得し、タイ語の認証翻訳を作成します。",
    ],
    checklist: [
      "原本（または発行機関の認証済み写し）。",
      "パスポートまたはIDカード。",
      "代理手続きの場合は委任状。",
      "使用する国・提出先機関と使用目的。",
      "海外書類の場合はアポスティーユ付きの原本。",
    ],
    pitfalls: [
      "「タイのアポスティーユ」を求めてしまう — 存在しません。",
      "翻訳を先に済ませたが、原本の再取得が必要になり無駄になる。",
      "在タイ大使館の認証工程を日程に入れていない。",
      "提出先が英語以外の言語を求めているのに、英訳だけを用意する。",
    ],
    remote:
      "経路の確認・翻訳・提出は遠隔で対応できます。委任状の署名のみご本人の関与が必要です。",
  },
};

/** 主要な県・地域の日本語表記（未収載の場合は英語名を使用）。 */
export const NEAR_JA_AREA: Record<string, string> = {
  nonthaburi: "ノンタブリー",
  pathumthani: "パトゥムターニー",
  samutprakan: "サムットプラーカーン",
  chonburi: "チョンブリー",
  pattaya: "パタヤ",
  rayong: "ラヨーン",
  chiangmai: "チェンマイ",
  chiangrai: "チェンライ",
  khonkaen: "コンケン",
  udonthani: "ウドンターニー",
  nakhonratchasima: "ナコンラーチャシーマー（コラート）",
  phuket: "プーケット",
  suratthani: "スラートターニー",
  songkhla: "ソンクラー",
  ubonratchathani: "ウボンラーチャターニー",
};
